import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX } from 'react-icons/fi';
import { TbTrash } from 'react-icons/tb';
import axios from 'axios';
import { useAuth } from '../../services/check_login';
import '../../../assets/css/placeholder-management.css';

const PlaceholderManagement = () => {
    const { user: currentUser } = useAuth();
    const [placeholders, setPlaceholders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState({ show: false, message: '', type: '' });
    const [editingId, setEditingId] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [formData, setFormData] = useState({ title: '', placeholder: '' });
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [placeholderToDelete, setPlaceholderToDelete] = useState(null);

    // Permission checks
    const permissions = currentUser?.role?.permissions || {};
    const canCreate = permissions.canCreatePlaceholders || currentUser?.isAdmin;
    const canEdit = permissions.canEditPlaceholders || currentUser?.isAdmin;
    const canDelete = permissions.canDeletePlaceholders || currentUser?.isAdmin;

    const showNotification = (message, type = 'success') => {
        setNotification({ show: true, message, type });
        setTimeout(() => {
            setNotification({ show: false, message: '', type: '' });
        }, 4000);
    };

    useEffect(() => {
        fetchPlaceholders();
    }, []);

    const fetchPlaceholders = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/placeholders', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPlaceholders(response.data);
        } catch (error) {
            console.error('Error fetching placeholders:', error);
            showNotification('Failed to load placeholders', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setIsAdding(true);
        setEditingId(null);
        setFormData({ title: '', placeholder: '' });
    };

    const handleEdit = (placeholder) => {
        setEditingId(placeholder._id);
        setIsAdding(false);
        setFormData({ title: placeholder.title, placeholder: placeholder.placeholder });
    };

    const handleCancel = () => {
        setIsAdding(false);
        setEditingId(null);
        setFormData({ title: '', placeholder: '' });
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('token');

            const dataToSave = {
                title: formData.title.trim(),
                placeholder: formData.placeholder.trim()
            };

            if (editingId) {
                // Update existing
                await axios.put(`/api/placeholders/${editingId}`, dataToSave, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showNotification('Placeholder updated successfully');
            } else {
                // Create new
                await axios.post('/api/placeholders', dataToSave, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showNotification('Placeholder created successfully');
            }

            fetchPlaceholders();
            handleCancel();
        } catch (error) {
            console.error('Error saving placeholder:', error);
            showNotification(error.response?.data?.error || 'Failed to save placeholder', 'error');
        }
    };

    const handleDelete = (placeholder) => {
        setPlaceholderToDelete(placeholder);
        setIsDeleteModalOpen(true);
    };

    const confirmDeletePlaceholder = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/placeholders/${placeholderToDelete._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showNotification('Placeholder deleted successfully');
            fetchPlaceholders();
            setIsDeleteModalOpen(false);
            setPlaceholderToDelete(null);
        } catch (error) {
            console.error('Error deleting placeholder:', error);
            showNotification('Failed to delete placeholder', 'error');
            setIsDeleteModalOpen(false);
            setPlaceholderToDelete(null);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    if (loading) {
        return <div className="placeholder-management"><div className="loading">Loading placeholders...</div></div>;
    }

    return (
        <div className="placeholder-management">
            {notification.show && (
                <div className={`notification ${notification.type}`}>
                    {notification.message}
                </div>
            )}
            
            <div className="placeholder-header">
                <h2>Placeholder Management</h2>
                {canCreate && (
                    <button className="btn-add" onClick={handleAdd} disabled={isAdding || editingId}>
                        <FiPlus /> Add Placeholder
                    </button>
                )}
            </div>

            <div className="placeholder-description">
                <p>Create custom placeholders to use in your content. Placeholders can be inserted into any text field when creating content.</p>
                <p><strong>Example:</strong> Create a placeholder with title "First Name" and value "First_Name" or {'"{{First_Name}}"'} - you decide the format!</p>
            </div>

            {(isAdding || editingId) && (
                <div className="placeholder-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label>Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="e.g., First Name"
                                className="form-control"
                            />
                        </div>
                        <div className="form-group">
                            <label>Placeholder</label>
                            <input
                                type="text"
                                name="placeholder"
                                value={formData.placeholder}
                                onChange={handleInputChange}
                                placeholder="e.g., First_Name or {{First_Name}} or %FIRSTNAME%"
                                className="form-control"
                            />
                        </div>
                    </div>
                    <div className="form-actions">
                        <button className="btn-save" onClick={handleSave} disabled={!formData.title || !formData.placeholder}>
                            <FiSave /> Save
                        </button>
                        <button className="btn-cancel" onClick={handleCancel}>
                            <FiX /> Cancel
                        </button>
                    </div>
                </div>
            )}

            <div className="placeholder-list">
                {placeholders.length === 0 ? (
                    <div className="no-placeholders">
                        No placeholders yet. Click "Add Placeholder" to create your first one.
                    </div>
                ) : (
                    <table className="placeholder-table">
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>Placeholder</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {placeholders.map(placeholder => (
                                <tr key={placeholder._id}>
                                    <td>{placeholder.title}</td>
                                    <td><code>{placeholder.placeholder}</code></td>
                                    <td>{new Date(placeholder.createdAt).toLocaleDateString()}</td>
                                    <td>
                                        <div className="placeholder-action-buttons">
                                            {canEdit && (
                                                <button
                                                    className="row-modify-btn"
                                                    onClick={() => handleEdit(placeholder)}
                                                    disabled={isAdding || editingId}
                                                    title="Edit"
                                                >
                                                    <FiEdit2 />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    className="row-remove-btn"
                                                    onClick={() => handleDelete(placeholder)}
                                                    disabled={isAdding || editingId}
                                                    title="Delete"
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content delete-modal">
                        <button 
                            className="modal-close-button" 
                            onClick={() => {
                                setIsDeleteModalOpen(false);
                                setPlaceholderToDelete(null);
                            }}
                            type="button"
                        >
                            ✕
                        </button>
                        <h2>Delete Placeholder</h2>
                        <div className="delete-confirmation-container">
                            <p className="delete-confirmation-message">
                                Are you sure you want to delete the placeholder <strong>{placeholderToDelete?.title}</strong>?
                            </p>
                            <p className="delete-warning-text">
                                This placeholder will no longer be available in the dropdown menus.
                            </p>
                            <div className="modal-actions">
                                <button 
                                    onClick={() => {
                                        setIsDeleteModalOpen(false);
                                        setPlaceholderToDelete(null);
                                    }} 
                                    className="btn-cancel"
                                >
                                    Cancel
                                </button>
                                <button onClick={confirmDeletePlaceholder} className="btn-delete">
                                    <TbTrash /> Delete Placeholder
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlaceholderManagement;
